import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Snackbar,
    Paper,
    Typography,
    Button,
    Stack,
    Box
} from '@mui/material';
import { Cookie } from '@mui/icons-material';
import { glassStyle } from '../theme';

const CONSENT_KEY = 'analytics_consent';
const CONSENT_DATE_KEY = 'analytics_consent_date';
const SESSION_ID_KEY = 'analytics_session_id';
const CONSENT_EXPIRY_DAYS = 365;

function generateSessionId() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : ((r & 0x3) | 0x8);
        return v.toString(16);
    });
}

export function getConsentStatus() {
    const consent = localStorage.getItem(CONSENT_KEY);
    const consentDate = localStorage.getItem(CONSENT_DATE_KEY);

    if (!consent || !consentDate) {
        return 'pending';
    }

    // Check if consent has expired
    const daysSinceConsent = (Date.now() - new Date(consentDate).getTime()) / (1000 * 60 * 60 * 24);
    if (daysSinceConsent > CONSENT_EXPIRY_DAYS) {
        localStorage.removeItem(CONSENT_KEY);
        localStorage.removeItem(CONSENT_DATE_KEY);
        return 'pending';
    }

    return consent;
}

export function getSessionId() {
    let sessionId = localStorage.getItem(SESSION_ID_KEY);
    if (!sessionId) {
        sessionId = generateSessionId();
        localStorage.setItem(SESSION_ID_KEY, sessionId);
    }
    return sessionId;
}

export default function CookieConsent({ onConsentChange }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const status = getConsentStatus();
        if (status === 'pending') {
            // Small delay to not show immediately on page load
            const timer = setTimeout(() => setOpen(true), 1000);
            return () => clearTimeout(timer);
        }
    }, []);

    const handleAccept = () => {
        localStorage.setItem(CONSENT_KEY, 'accepted');
        localStorage.setItem(CONSENT_DATE_KEY, new Date().toISOString());
        getSessionId(); // Generate session ID
        setOpen(false);
        onConsentChange?.('accepted');
    };

    const handleDecline = () => {
        localStorage.setItem(CONSENT_KEY, 'declined');
        localStorage.setItem(CONSENT_DATE_KEY, new Date().toISOString());
        setOpen(false);
        onConsentChange?.('declined');
    };

    return (
        <Snackbar
            open={open}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            sx={{ maxWidth: 600 }}
        >
            <Paper
                elevation={8}
                sx={{
                    ...glassStyle,
                    p: 3,
                    borderRadius: 3,
                    maxWidth: 500
                }}
            >
                <Stack spacing={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Cookie color="primary" />
                        <Typography variant="h6" fontWeight="bold">
                            {t('cookie_consent_title', 'We value your privacy')}
                        </Typography>
                    </Box>

                    <Typography variant="body2" color="text.secondary">
                        {t('cookie_consent_message', 'We use analytics cookies to understand how you use our app and improve your experience. Your IP address will be anonymized.')}
                    </Typography>

                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <Button
                            variant="text"
                            color="inherit"
                            onClick={handleDecline}
                        >
                            {t('cookie_consent_decline', 'Decline')}
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleAccept}
                        >
                            {t('cookie_consent_accept', 'Accept')}
                        </Button>
                    </Stack>
                </Stack>
            </Paper>
        </Snackbar>
    );
}
